import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SpeakerWaveIcon, PlayIcon, StopIcon, MicrophoneIcon } from '@heroicons/react/24/outline';
import type { IDialogueTone } from '@packages/global';
import DialogueToneSelector from '../../common/DialogueToneSelector';
import { episodeScriptApi } from '../../../services/user';

interface NarratorCardProps {
  narratorVoiceUrl: string;
  scriptId: string;
  playingAudio: string | null;
  onPlayAudio: (url: string, id: string) => void;
  onUpdate?: () => void;
}

export default function NarratorCard({ narratorVoiceUrl, scriptId, playingAudio, onPlayAudio, onUpdate }: NarratorCardProps) {
  const { t } = useTranslation(['script', 'common']);
  const [showToneSelector, setShowToneSelector] = useState(false);
  const [updating, setUpdating] = useState(false);
  const isPlaying = playingAudio === 'narrator';

  const handleSelectVoice = async (tone: IDialogueTone) => {
    if (!tone.previewAudioUrl) return;
    try {
      setUpdating(true);
      await episodeScriptApi.updateNarratorVoice(scriptId, tone.previewAudioUrl);
      setShowToneSelector(false);
      onUpdate?.();
    } catch (error: any) {
      console.error('Failed to update narrator voice:', error);
      alert(error.message || t('narrator.updateVoiceFailed'));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <div className="bg-bg-surface rounded-lg overflow-hidden group">
        {/* 正方形图片区域 - 用旁白图标代替 */}
        <div className="relative w-full aspect-square bg-gradient-to-br from-blue-900/60 to-indigo-900/60 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center">
              <SpeakerWaveIcon className="w-8 h-8 text-blue-400" />
            </div>
            <span className="text-xs text-blue-300/70">{t('shots.narrator')}</span>
          </div>
        </div>

        <div className="p-3 space-y-2">
          <div className="text-sm font-semibold text-gray-50">{t('shots.narrator')}</div>
          <div className="text-xs text-gray-400 truncate">{t('narrator.description')}</div>
          {/* 操作按钮 - 与 CharacterCard 一致 */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPlayAudio(narratorVoiceUrl, 'narrator')}
              className="p-1.5 bg-bg-elevated hover:bg-bg-surface rounded-md transition-colors cursor-pointer"
              title={isPlaying ? t('narrator.stopPlay') : t('narrator.playVoice')}
            >
              {isPlaying
                ? <StopIcon className="w-3.5 h-3.5 text-green-400" />
                : <PlayIcon className="w-3.5 h-3.5 text-gray-300" />}
            </button>
            <button
              onClick={() => setShowToneSelector(true)}
              disabled={updating}
              className="p-1.5 bg-bg-elevated hover:bg-bg-surface rounded-md transition-colors disabled:opacity-50 cursor-pointer"
              title={t('narrator.changeVoice')}
            >
              <MicrophoneIcon className="w-3.5 h-3.5 text-gray-300" />
            </button>
          </div>
        </div>
      </div>

      <DialogueToneSelector
        isOpen={showToneSelector}
        onClose={() => setShowToneSelector(false)}
        onSelect={handleSelectVoice}
      />
    </>
  );
}
